import { SystemFunction } from '../api/permissionGroupsApi';

// Map action to function code suffix
const ACTION_TO_SUFFIX: Record<string, string[]> = {
  view: ['_LIST', '_VIEW'],
  create: ['_ADD'],
  edit: ['_EDIT'],
  delete: ['_DELETE'],
  import: ['_IMPORT'],
};

/**
 * Report / payment screens use one Function row; permissions are stored as actions on that code
 * (e.g. TRANSACTION_REPORT + edit), not as TRANSACTION_REPORT_EDIT.
 */
const STANDALONE_FUNCTION_ACTIONS: Record<string, string[]> = {
  TRANSACTION_REPORT: ['view', 'edit'],
  SERVICE_PAYMENT: ['view', 'create', 'edit'],
  RECEIVABLE_REPORT: ['view'],
  /** POST /pdf-exports/request requires create; view alone is legacy from UI that only offered view. */
  EXPORT_PDF_SERVICE_FEES: ['view', 'create'],
  IE_REVENUE_ACTIVITIES: ['view', 'create', 'edit', 'delete'],
  IE_EXPENDITURE_ACTIVITIES: ['view', 'create', 'edit', 'delete'],
};

function getStandaloneFunctionActions(func: SystemFunction): string[] {
  const configured = STANDALONE_FUNCTION_ACTIONS[func.code];
  if (configured) {
    return configured;
  }
  if (func.code.endsWith('_IMPORT')) {
    return ['view', 'import'];
  }
  return ['view'];
}

// Extract base name from function code (e.g., ACCOUNT_LIST -> ACCOUNT)
export function getBaseName(code: string): string {
  // Remove common suffixes in order of specificity (longer first)
  const suffixes = ['_DETAILS', '_REMOVE', '_DELETE', '_EDIT', '_ADD', '_IMPORT', '_VIEW', '_LIST'];
  for (const suffix of suffixes) {
    if (code.endsWith(suffix)) {
      return code.slice(0, -suffix.length);
    }
  }
  return code;
}

// Get function code for a base name and action
export function getFunctionCode(baseName: string, action: string): string {
  const suffixes = ACTION_TO_SUFFIX[action] || [];
  // Try to find existing function with this base name and suffix
  for (const suffix of suffixes) {
    const code = baseName + suffix;
    return code; // Return first match (prefer _LIST over _VIEW for view)
  }
  return baseName; // Fallback
}

// Group functions by base name
export interface GroupedFunction {
  baseName: string;
  baseCode: string;
  displayName: string;
  functions: SystemFunction[];
  // Map of action -> function
  actionToFunction: Record<string, SystemFunction | null>;
}

// Check if a function code follows CRUD pattern
function hasCrudSuffix(code: string): boolean {
  const crudSuffixes = ['_LIST', '_VIEW', '_ADD', '_EDIT', '_DELETE', '_REMOVE', '_IMPORT'];
  return crudSuffixes.some(suffix => code.endsWith(suffix));
}

// Check if a base name has multiple CRUD functions
function hasMultipleCrudFunctions(baseName: string, functions: SystemFunction[]): boolean {
  const crudFunctions = functions.filter(f => {
    const base = getBaseName(f.code);
    return base === baseName && hasCrudSuffix(f.code);
  });
  return crudFunctions.length > 1;
}

// Normalize function code for grouping (handles variations like FEE_CATEGORY vs FEE_CATEGORIES)
function normalizeCodeForGrouping(code: string): string {
  // Remove common suffixes first
  let normalized = getBaseName(code);
  
  // Handle plural/singular variations (CATEGORY vs CATEGORIES)
  if (normalized.endsWith('IES')) {
    normalized = normalized.slice(0, -3) + 'Y';
  } else if (normalized.endsWith('S') && !normalized.endsWith('SS')) {
    // Remove trailing S (but keep SS like CLASS)
    const withoutS = normalized.slice(0, -1);
    // Only normalize if it makes sense (not for codes ending in S naturally)
    if (withoutS.length > 3) {
      normalized = withoutS;
    }
  }
  
  return normalized.toUpperCase();
}

// Check if two codes should be grouped together
function shouldGroupCodes(code1: string, code2: string): boolean {
  const base1 = getBaseName(code1);
  const base2 = getBaseName(code2);
  
  // Exact match
  if (base1 === base2) return true;
  
  // Normalized match (handles FEE_CATEGORY vs FEE_CATEGORIES)
  const norm1 = normalizeCodeForGrouping(code1);
  const norm2 = normalizeCodeForGrouping(code2);
  if (norm1 === norm2) return true;
  
  // One is base, other has suffix (e.g., ENTRY_EXIT_REGISTRATION and ENTRY_EXIT_REGISTRATION_ADD)
  if (code1 === base2 || code2 === base1) return true;
  
  return false;
}

export function groupFunctions(functions: SystemFunction[]): GroupedFunction[] {
  // Step 1: Deduplicate by exact code (case-insensitive)
  const codeMap = new Map<string, SystemFunction>();
  for (const func of functions) {
    const key = func.code.toUpperCase();
    if (!codeMap.has(key)) {
      codeMap.set(key, func);
    } else {
      // Keep the one with better name (prefer capitalized)
      const existing = codeMap.get(key)!;
      if (func.name !== func.name.toLowerCase() && existing.name === existing.name.toLowerCase()) {
        codeMap.set(key, func);
      }
    }
  }
  const uniqueFunctions = Array.from(codeMap.values());

  // Step 2: Group functions intelligently
  const groupedMap = new Map<string, GroupedFunction>();
  const processed = new Set<string>();

  for (const func of uniqueFunctions) {
    if (processed.has(func.code)) continue;

    const baseName = getBaseName(func.code);
    const hasCrud = hasCrudSuffix(func.code);
    
    // Find all related functions (same base or variations)
    const relatedFunctions = uniqueFunctions.filter(f => {
      if (processed.has(f.code)) return false;
      return shouldGroupCodes(func.code, f.code);
    });

    // Only group if we have multiple related functions OR if base function exists with CRUD variants
    const hasBaseFunction = relatedFunctions.some(f => !hasCrudSuffix(f.code));
    const hasCrudVariants = relatedFunctions.some(f => hasCrudSuffix(f.code));
    
    if (relatedFunctions.length > 1 || (hasBaseFunction && hasCrudVariants)) {
      // Group these functions
      const groupKey = normalizeCodeForGrouping(func.code);
      
      if (!groupedMap.has(groupKey)) {
        // Find the best main function (prefer base, then _LIST, then _VIEW)
        const mainFunction = relatedFunctions.find(f => !hasCrudSuffix(f.code)) ||
                           relatedFunctions.find(f => f.code.endsWith('_LIST')) ||
                           relatedFunctions.find(f => f.code.endsWith('_VIEW')) ||
                           func;

        // Clean display name
        let displayName = mainFunction.name;
        displayName = displayName.replace(/^(Danh sách|Thêm|Sửa|Xóa|Chi tiết|Cập nhật)\s+/i, '').trim();
        
        if (displayName === mainFunction.name || !displayName) {
          const listFunc = relatedFunctions.find(f => f.code.endsWith('_LIST'));
          if (listFunc) {
            displayName = listFunc.name.replace(/^Danh sách\s+/i, '').trim() || listFunc.name;
          }
        }
        
        groupedMap.set(groupKey, {
          baseName: groupKey,
          baseCode: groupKey,
          displayName: displayName || mainFunction.name,
          functions: [],
          actionToFunction: {},
        });
      }

      const group = groupedMap.get(groupKey)!;
      for (const relatedFunc of relatedFunctions) {
        group.functions.push(relatedFunc);
        processed.add(relatedFunc.code);
      }
    } else {
      // Standalone function
      processed.add(func.code);
    }
  }

  // Step 3: Map actions to functions for grouped items
  const grouped = Array.from(groupedMap.values());
  for (const group of grouped) {
    // For each action, find the corresponding function
    for (const [action, suffixes] of Object.entries(ACTION_TO_SUFFIX)) {
      for (const suffix of suffixes) {
        // Try exact match first
        let func = group.functions.find(f => f.code === group.baseCode + suffix);
        
        // If not found, try with normalized base
        if (!func) {
          for (const f of group.functions) {
            const fBase = getBaseName(f.code);
            if (f.code === fBase + suffix) {
              func = f;
              break;
            }
          }
        }
        
        if (func) {
          group.actionToFunction[action] = func;
          break;
        }
      }
    }
    
    // For base functions without CRUD suffix:
    // - Only map VIEW action if no LIST/VIEW function exists
    // - Don't map CREATE/EDIT/DELETE unless specific functions exist
    const baseFunc = group.functions.find(f => !hasCrudSuffix(f.code));
    if (baseFunc) {
      // Only map VIEW to base function if no LIST/VIEW variant exists
      if (!group.actionToFunction['view']) {
        group.actionToFunction['view'] = baseFunc;
      }
      // Don't map other actions to base function - they must have specific functions
    }
  }

  // Step 4: Create entries for standalone functions (those not in any group)
  // We need to check which functions weren't added to any group
  const allGroupedFunctionCodes = new Set<string>();
  for (const group of grouped) {
    for (const func of group.functions) {
      allGroupedFunctionCodes.add(func.code);
    }
  }
  
  for (const func of uniqueFunctions) {
    if (!allGroupedFunctionCodes.has(func.code)) {
      const actionToFunction: Record<string, SystemFunction | null> = {};
      for (const action of getStandaloneFunctionActions(func)) {
        actionToFunction[action] = func;
      }
      const standaloneGroup: GroupedFunction = {
        baseName: func.code,
        baseCode: func.code,
        displayName: func.name,
        functions: [func],
        actionToFunction,
      };
      grouped.push(standaloneGroup);
    }
  }

  // Sort by base code
  return grouped.sort((a, b) => a.baseCode.localeCompare(b.baseCode));
}

// Get permission for a grouped function and action
export function getGroupedPermission(
  permissions: { functionId: string; actions: string[] }[],
  groupedFunction: GroupedFunction,
  action: string
): boolean {
  const func = groupedFunction.actionToFunction[action];
  if (!func) return false;
  
  const permission = permissions.find(p => p.functionId === func.id);
  if (!permission) return false;
  
  // For standalone functions (only one function in group), check if action is in permissions
  if (groupedFunction.functions.length === 1) {
    return permission.actions.includes(action);
  }
  
  // For grouped CRUD functions, check if action is in permissions
  return permission.actions.includes(action);
}

// Update permission for a grouped function and action
export function updateGroupedPermission(
  permissions: { functionId: string; actions: string[] }[],
  groupedFunction: GroupedFunction,
  action: string,
  checked: boolean
): { functionId: string; actions: string[] }[] {
  const func = groupedFunction.actionToFunction[action];
  if (!func) return permissions;

  // Find or create permission for this function
  let permission = permissions.find(p => p.functionId === func.id);
  
  if (!permission) {
    permission = { functionId: func.id, actions: [] };
    permissions.push(permission);
  }

  // Update actions
  if (checked) {
    if (!permission.actions.includes(action)) {
      permission.actions.push(action);
    }
  } else {
    permission.actions = permission.actions.filter(a => a !== action);
    // Remove permission entry if no actions left
    if (permission.actions.length === 0) {
      const index = permissions.indexOf(permission);
      if (index > -1) {
        permissions.splice(index, 1);
      }
    }
  }

  return permissions;
}

